import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory } from 'react-router-dom';
import { AlertTriangle, Clock, CheckCircle2, XCircle, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';

import {
  getQuizAttempt,
  submitQuizAttempt,
  reportQuizTabBlur,
  saveQuizAnswer,
} from '../../../services/trainingService';
import Button from '../../../components/ui/Button';
import CheckboxInput from '../../../components/ui/CheckboxInput';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';

const Container = styled.div`padding: 24px; max-width: 900px; margin: 0 auto; position: relative;`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const SubmittingOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 42, 42, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const SubmittingCard = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 28px 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  box-shadow: 0 4px 16px rgba(0, 66, 66, 0.15);
`;

const Spinner = styled(Loader2)`
  animation: ${spin} 1s linear infinite;
  color: ${({ theme }) => theme.colors.primary.main};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const TimerBox = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: ${({ $danger }) => ($danger ? '#FEE2E2' : '#E0F2FE')};
  color: ${({ $danger }) => ($danger ? '#991B1B' : '#075985')};
  border-radius: 6px;
  padding: 6px 12px;
  font-weight: 700;
  font-size: 14px;
`;

const BlurWarning = styled.div`
  background: #FEF3C7;
  color: #92400E;
  border: 1px solid #FCD34D;
  border-radius: 6px;
  padding: 10px 14px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 20px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
`;

const QuestionText = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 16px;
  white-space: pre-wrap;
`;

const OptionRow = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  border: 1.5px solid ${({ $selected, theme }) => ($selected ? theme.colors.primary.main : '#E0E4E8')};
  border-radius: 6px;
  margin-bottom: 8px;
  cursor: pointer;
  background: ${({ $selected }) => ($selected ? '#EFF6FF' : '#fff')};
  transition: all 0.15s;
`;

const NavRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 20px;
  gap: 12px;
`;

const Dots = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: center;
  margin-bottom: 16px;
`;

const Dot = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: 1.5px solid ${({ $active, $answered, theme }) => (
    $active ? theme.colors.primary.main : $answered ? theme.colors.secondary.main : '#CBD5E1'
  )};
  background: ${({ $active, $answered, theme }) => (
    $active ? theme.colors.primary.main : $answered ? '#D1FAE5' : '#fff'
  )};
  color: ${({ $active }) => ($active ? '#fff' : '#1E293B')};
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
`;

const ResultCard = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 32px;
  text-align: center;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
`;

const ResultBadge = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${({ $passed }) => ($passed ? '#D1FAE5' : '#FEE2E2')};
  color: ${({ $passed }) => ($passed ? '#065F46' : '#991B1B')};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
`;

/**
 * Quiz session page. URL: /my-trainings/:assignmentId/quiz/:attemptId
 *
 * Core behaviour:
 *  - Pulls the attempt payload (questions, options, deadline, tab-blur state).
 *  - Renders one question at a time with a "backtrack" nav strip (if allowed).
 *  - Fires a server tab-blur when visibilitychange goes to hidden; terminates
 *    the attempt after 3 strikes (server-authoritative).
 *  - Client-side countdown timer auto-submits when it hits 0 (server also
 *    enforces deadline on submit).
 */
export default function QuizSessionPage() {
  const { t } = useTranslation();
  const history = useHistory();
  const { assignmentId, attemptId } = useParams();

  const [attempt, setAttempt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState({}); // { questionId: Set<optionId> }
  const [result, setResult] = useState(null);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [blurWarning, setBlurWarning] = useState(null);
  const [remainingSec, setRemainingSec] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const submittingRef = useRef(false);
  const terminatedRef = useRef(false);
  const resultRef = useRef(null);

  // ----- Load attempt -----
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getQuizAttempt(attemptId);
        if (cancelled) return;
        const data = res?.data?.data;
        setAttempt(data);
        if (data?.deadlineAt) {
          const remaining = Math.floor((new Date(data.deadlineAt).getTime() - Date.now()) / 1000);
          setRemainingSec(Math.max(0, remaining));
        }
        // Rehydrate previously-saved selections (QA R114 — learner can resume).
        const restored = {};
        (data?.questions || []).forEach((q) => {
          if (Array.isArray(q.selectedOptionIds) && q.selectedOptionIds.length > 0) {
            restored[q.questionId] = new Set(q.selectedOptionIds);
          }
        });
        if (Object.keys(restored).length > 0) setAnswers(restored);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [attemptId]);

  // ----- Countdown -----
  useEffect(() => {
    if (remainingSec == null) return undefined;
    if (remainingSec <= 0) return undefined;
    const id = setInterval(() => {
      setRemainingSec((s) => {
        if (s == null) return s;
        if (s <= 1) {
          clearInterval(id);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [remainingSec]);

  // ----- Tab blur anti-cheat -----
  const doSubmit = useCallback(async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    const payload = Object.entries(answers).map(([questionId, set]) => ({
      questionId,
      selectedOptionIds: Array.from(set || []),
    }));
    try {
      const res = await submitQuizAttempt(attemptId, payload);
      const data = res?.data?.data;
      setResult(data);
      resultRef.current = data;
    } catch {
      submittingRef.current = false;
    } finally {
      setSubmitting(false);
    }
  }, [answers, attemptId]);

  useEffect(() => {
    if (!attempt?.antiCheatEnabled || result || terminatedRef.current) return undefined;

    const onVis = async () => {
      if (document.visibilityState !== 'hidden') return;
      try {
        const res = await reportQuizTabBlur(attemptId);
        const data = res?.data?.data;
        if (!data) return;
        if (data.terminated) {
          terminatedRef.current = true;
          const r = {
            attemptId,
            terminatedReason: 'anti_cheat_violation',
            passed: false,
            remainingAttempts: -2,
            score: 0,
            maxScore: 0,
            scorePercent: 0,
            passingScore: attempt?.passingScore,
          };
          resultRef.current = r;
          setResult(r);
        } else {
          setBlurWarning({ count: data.tabBlurCount });
          setTimeout(() => setBlurWarning(null), 6000);
        }
      } catch { /* noop */ }
    };

    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [attempt, attemptId, result]);

  // ----- Auto-submit on time expiry -----
  useEffect(() => {
    if (remainingSec === 0 && attempt && !result && !terminatedRef.current) {
      doSubmit();
    }
  }, [remainingSec, attempt, result, doSubmit]);

  const questions = attempt?.questions || [];
  const current = questions[index];

  const toggle = (questionId, optionId, multi) => {
    setAnswers((prev) => {
      const existing = new Set(prev[questionId] || []);
      if (multi) {
        if (existing.has(optionId)) existing.delete(optionId);
        else existing.add(optionId);
      } else {
        existing.clear();
        existing.add(optionId);
      }
      const next = { ...prev, [questionId]: existing };
      // Autosave — fire-and-forget, interceptor handles errors.
      saveQuizAnswer(attemptId, questionId, Array.from(existing)).catch(() => {});
      return next;
    });
  };

  const answeredCount = useMemo(
    () => Object.values(answers).filter((set) => set && set.size > 0).length,
    [answers]
  );

  const formatTimer = (secs) => {
    if (secs == null) return null;
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  if (loading) return <Container>{t('common.loading')}</Container>;
  if (!attempt) return null;

  // ----- Result screen -----
  if (result) {
    const passed = result.passed;
    const terminated = result.terminatedReason === 'anti_cheat_violation';
    return (
      <Container>
        <ResultCard>
          <ResultBadge $passed={passed}>
            {passed ? <CheckCircle2 size={40} /> : <XCircle size={40} />}
          </ResultBadge>
          <h2 style={{ margin: '0 0 8px' }}>
            {terminated ? t('training.quizTerminated')
              : passed ? t('training.quizPassedTitle')
              : t('training.quizFailedTitle')}
          </h2>
          {!terminated && (
            <div style={{ color: '#475569', marginBottom: 20 }}>
              {t('training.quizScoreLine', {
                score: result.scorePercent,
                pass: result.passingScore,
              })}
            </div>
          )}
          {terminated && (
            <div style={{ color: '#475569', marginBottom: 20 }}>
              {t('training.quizTerminatedReason')}
            </div>
          )}
          {result.remainingAttempts != null && result.remainingAttempts >= 0 && !passed && (
            <div style={{ color: '#64748B', marginBottom: 16, fontSize: 13 }}>
              {t('training.quizRemainingAttempts', { n: result.remainingAttempts })}
            </div>
          )}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button type="button" $variant="outline" onClick={() => history.push(`/my-trainings/${assignmentId}`)}>
              {t('training.backToTraining')}
            </Button>
          </div>
        </ResultCard>
      </Container>
    );
  }

  const isMulti = current?.questionType === 'multiple_choice';
  const selected = answers[current.questionId] || new Set();
  const timerDanger = remainingSec != null && remainingSec > 0 && remainingSec <= 60;

  return (
    <Container>
      <Header>
        <h2 style={{ margin: 0 }}>{t('training.quizInProgress')}</h2>
        {remainingSec != null && (
          <TimerBox $danger={timerDanger}>
            <Clock size={14} />
            {formatTimer(remainingSec)}
          </TimerBox>
        )}
      </Header>

      {blurWarning && (
        <BlurWarning>
          <AlertTriangle size={16} />
          {t('training.tabBlurWarning', { n: blurWarning.count, max: 3 })}
        </BlurWarning>
      )}

      {attempt.allowBacktrack && (
        <Dots>
          {questions.map((q, i) => {
            const answered = answers[q.questionId]?.size > 0;
            return (
              <Dot
                key={q.questionId}
                type="button"
                $active={i === index}
                $answered={answered}
                onClick={() => setIndex(i)}
              >
                {i + 1}
              </Dot>
            );
          })}
        </Dots>
      )}

      <Card>
        <div style={{ fontSize: 12, color: '#64748B', marginBottom: 6 }}>
          {t('training.questionPosition', { n: index + 1, total: questions.length })}
          {' · '}
          {t('training.pointsShort', { n: current.points })}
        </div>
        <QuestionText>{current.questionText}</QuestionText>

        {current.options.map((opt) => {
          const isSelected = selected.has(opt.optionId);
          return (
            <OptionRow key={opt.optionId} $selected={isSelected}>
              {isMulti ? (
                <CheckboxInput
                  checked={isSelected}
                  onChange={() => toggle(current.questionId, opt.optionId, true)}
                />
              ) : (
                <input
                  type="radio"
                  name={current.questionId}
                  checked={isSelected}
                  onChange={() => toggle(current.questionId, opt.optionId, false)}
                />
              )}
              <span>{opt.optionText}</span>
            </OptionRow>
          );
        })}

        <NavRow>
          <Button
            type="button"
            $variant="outline"
            disabled={!attempt.allowBacktrack || index === 0}
            onClick={() => setIndex((i) => Math.max(0, i - 1))}
          >
            <ChevronLeft size={16} /> {t('common.previous')}
          </Button>
          <div style={{ color: '#64748B', fontSize: 13 }}>
            {t('training.answeredLine', { done: answeredCount, total: questions.length })}
          </div>
          {index === questions.length - 1 ? (
            <Button
              type="button"
              $variant="primary"
              onClick={() => setConfirmSubmit(true)}
              disabled={submitting}
            >
              {t('training.submitQuiz')}
            </Button>
          ) : (
            <Button type="button" $variant="primary" onClick={() => setIndex((i) => Math.min(questions.length - 1, i + 1))}>
              {t('common.next')} <ChevronRight size={16} />
            </Button>
          )}
        </NavRow>
      </Card>

      <ConfirmDialog
        isOpen={confirmSubmit}
        title={t('training.submitQuiz')}
        message={t('training.submitQuizConfirm', { done: answeredCount, total: questions.length })}
        onConfirm={() => { setConfirmSubmit(false); doSubmit(); }}
        onCancel={() => setConfirmSubmit(false)}
        confirmText={t('training.submitQuiz')}
        cancelText={t('common.cancel')}
        type="info"
      />

      {submitting && (
        <SubmittingOverlay>
          <SubmittingCard>
            <Spinner size={32} />
            <div style={{ fontSize: 14, fontWeight: 600, color: '#004242' }}>
              {t('training.quizSubmitting')}
            </div>
            <div style={{ fontSize: 12, color: '#64748b' }}>
              {t('training.quizSubmittingHint')}
            </div>
          </SubmittingCard>
        </SubmittingOverlay>
      )}
    </Container>
  );
}
